import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronRight, ChevronLeft } from "lucide-react";

/**
 * TrimSlider — dual-thumb range slider for picking [start, end] inside a
 * clip with total length `duration`. Enforces a minimum distance of
 * `minDistance` seconds between thumbs.
 *
 * UX (Phase 7 update after user feedback "Trim-UI ist unklar"):
 * - Each thumb has a VISIBLE LABEL ("Anfang" ▶| / "Ende" |◀) sitting above
 *   it, with an arrow icon hinting at the drag direction. The label is the
 *   first thing the user notices — no more "what are these two dots?"
 * - Hover tooltip "Ziehen um zu kürzen" on each thumb.
 * - Pointer + touch + keyboard (←/→ ±0.1 s, Shift+←/→ ±1 s).
 * - Live values propagated via onChange while dragging,
 *   `onChangeCommitted` fires once when pointer is released.
 */
export default function TrimSlider({
  duration,
  start,
  end,
  minDistance = 0.5,
  onChange,
  onChangeCommitted,
}) {
  const trackRef = useRef(null);
  const draggingRef = useRef(null); // "start" | "end" | null

  const safeDuration = Math.max(duration || 0, minDistance);

  const pctStart = (start / safeDuration) * 100;
  const pctEnd = (end / safeDuration) * 100;

  const valueFromClientX = useCallback((clientX) => {
    const track = trackRef.current;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return ratio * safeDuration;
  }, [safeDuration]);

  const updateThumb = useCallback((which, value) => {
    let nextStart = start;
    let nextEnd = end;
    if (which === "start") {
      nextStart = Math.min(Math.max(0, value), end - minDistance);
    } else {
      nextEnd = Math.max(Math.min(safeDuration, value), start + minDistance);
    }
    onChange?.({ start: nextStart, end: nextEnd });
  }, [start, end, minDistance, safeDuration, onChange]);

  const onPointerDownThumb = (which) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = which;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* pointer capture not supported */
    }
  };

  const onTrackPointerDown = (e) => {
    const v = valueFromClientX(e.clientX);
    const which = Math.abs(v - start) < Math.abs(v - end) ? "start" : "end";
    draggingRef.current = which;
    updateThumb(which, v);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* pointer capture not supported */
    }
  };

  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    const v = valueFromClientX(e.clientX);
    updateThumb(draggingRef.current, v);
  };

  const onPointerUp = () => {
    if (draggingRef.current) {
      draggingRef.current = null;
      onChangeCommitted?.({ start, end });
    }
  };

  const committedRef = useRef(onChangeCommitted);
  useEffect(() => { committedRef.current = onChangeCommitted; }, [onChangeCommitted]);

  const onKeyDownFactory = (which) => (e) => {
    const big = e.shiftKey ? 1 : 0.1;
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      const cur = which === "start" ? start : end;
      updateThumb(which, cur - big);
      committedRef.current?.({ start: which === "start" ? Math.max(0, start - big) : start, end });
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      const cur = which === "start" ? start : end;
      updateThumb(which, cur + big);
      committedRef.current?.({ start, end: which === "end" ? Math.min(safeDuration, end + big) : end });
    }
  };

  return (
    <div className="w-full select-none" data-testid="trim-slider">
      {/* Top labels */}
      <div className="flex items-center justify-between mb-1.5 text-xs">
        <span className="text-[#9a9aa3]">
          Start <span className="text-white tabular-nums ml-1" data-testid="trim-slider-start-label">{formatTime(start)}</span>
        </span>
        <span className="text-[#9a9aa3]">
          Dauer <span className="text-white tabular-nums ml-1" data-testid="trim-slider-duration-label">{formatTime(end - start)}</span>
        </span>
        <span className="text-[#9a9aa3]">
          Ende <span className="text-white tabular-nums ml-1" data-testid="trim-slider-end-label">{formatTime(end)}</span>
        </span>
      </div>

      {/* Slider with labelled thumbs */}
      <div className="relative pt-7 pb-4">
        {/* Anfang label above start thumb */}
        <div
          className="absolute -translate-x-1/2 top-0 text-[11px] font-semibold text-[#ff6b35] flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[#1f1f23] border border-[#ff6b35]/40 whitespace-nowrap pointer-events-none"
          style={{ left: `${pctStart}%` }}
          data-testid="trim-slider-start-tag"
        >
          <ChevronRight size={11} strokeWidth={3} />
          Anfang
        </div>
        {/* Ende label above end thumb */}
        <div
          className="absolute -translate-x-1/2 top-0 text-[11px] font-semibold text-[#ff3d6e] flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[#1f1f23] border border-[#ff3d6e]/40 whitespace-nowrap pointer-events-none"
          style={{ left: `${pctEnd}%` }}
          data-testid="trim-slider-end-tag"
        >
          <ChevronLeft size={11} strokeWidth={3} />
          Ende
        </div>

        <div
          ref={trackRef}
          className="relative h-9 cursor-pointer touch-none"
          onPointerDown={onTrackPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {/* Track base */}
          <div className="absolute inset-y-3 left-0 right-0 rounded-full bg-[#1f1f23]" />
          {/* Selected range */}
          <div
            className="absolute inset-y-3 rounded-full bg-gradient-to-r from-[#ff6b35] to-[#ff3d6e]"
            style={{ left: `${pctStart}%`, right: `${100 - pctEnd}%` }}
            data-testid="trim-slider-range"
          />
          {/* Start thumb */}
          <button
            type="button"
            aria-label="Anfang — ziehen um zu kürzen"
            title="Ziehen um zu kürzen"
            tabIndex={0}
            onPointerDown={onPointerDownThumb("start")}
            onKeyDown={onKeyDownFactory("start")}
            data-testid="trim-slider-thumb-start"
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-white border-2 border-[#ff6b35] shadow-lg cursor-grab active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-[#ff6b35]/60 hover:scale-110 transition-transform"
            style={{ left: `${pctStart}%` }}
          />
          {/* End thumb */}
          <button
            type="button"
            aria-label="Ende — ziehen um zu kürzen"
            title="Ziehen um zu kürzen"
            tabIndex={0}
            onPointerDown={onPointerDownThumb("end")}
            onKeyDown={onKeyDownFactory("end")}
            data-testid="trim-slider-thumb-end"
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-white border-2 border-[#ff3d6e] shadow-lg cursor-grab active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-[#ff3d6e]/60 hover:scale-110 transition-transform"
            style={{ left: `${pctEnd}%` }}
          />
        </div>
      </div>

      <div className="flex items-center justify-between mt-1 text-[10px] uppercase tracking-wider text-[#5a5a60]">
        <span>0:00</span>
        <span>Gesamtlänge {formatTime(safeDuration)}</span>
      </div>
    </div>
  );
}

function formatTime(seconds) {
  const s = Math.max(0, seconds || 0);
  const m = Math.floor(s / 60);
  const rem = s - m * 60;
  return `${m}:${rem.toFixed(1).padStart(4, "0")}`;
}
